import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronRight, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { toast } from 'react-toastify';

import { useIsReadOnly } from '@/utils/auth/roleChecker';
import useTenantState from '@/states/tenant.state';
import useCompanyState from '@/states/company.state';
import { useCompanyProfileSteps } from '@/hooks/useCompanyProfileSteps';
import useRewardState, { IReward } from '@/states/reward.state';
import { useGetOrgChartNames } from './sop/useSopMedia';
import { useRefetchCompletionStatus } from '@/hooks/useCompletionStatus';
import Punishment from './Punishment';
import {
  RewardFormValues,
  RewardRowValues,
  formatValidationErrors,
} from './reward-punishment/reward-punishment.schema';
import StepTipsPanel from '@/components/company/StepTipsPanel';

type Tab = 'reward' | 'punishment';

const CATEGORY_OPTIONS = [
  { label: '-- Pilih Kategori --', value: '' },
  { label: 'Perusahaan', value: 'level_perusahaan' },
  { label: 'Jabatan', value: 'level_jabatan' },
];

const PERIOD_OPTIONS = [
  { label: '-- Pilih Periode --', value: '' },
  { label: 'Harian', value: 'Harian' },
  { label: 'Mingguan', value: 'Mingguan' },
  { label: 'Bulanan', value: 'Bulanan' },
  { label: 'Tahunan', value: 'Tahunan' },
  { label: 'Insidental', value: 'Insidental' },
];

export default function RewardPunishment() {
  const navigate = useNavigate();
  const { tenant_id } = useParams<{ tenant_id: string }>();
  const isReadOnly = useIsReadOnly();
  const [tab, setTab] = useState<Tab>('reward');
  const [showPanel, setShowPanel] = useState(true);

  useCompanyProfileSteps();
  const { companyProfileSteps } = useCompanyState();
  const rewardStep = companyProfileSteps.find(step => step.step_name.trim().toLowerCase() === 'reward');
  const videoLink = rewardStep?.video_link;
  const tips = rewardStep?.tips;

  const { currentTenant } = useTenantState();
  const { rewards, getRewards, createReward, updateReward, deleteReward } = useRewardState();
  const refetchCompletionStatus = useRefetchCompletionStatus();
  const { data: orgChartNames = [] } = useGetOrgChartNames(currentTenant?.id);

  // Fetch rewards on mount
  useEffect(() => {
    if (currentTenant?.id) {
      getRewards(currentTenant.id, { page_size: 100 });
    }
  }, [currentTenant?.id, getRewards]);

  const [isSaving, setIsSaving] = useState(false);
  const [deletedIds, setDeletedIds] = useState<(number | string)[]>([]);
  const [validationErrors, setValidationErrors] = useState(formatValidationErrors([]));

  const toRowValues = (r: IReward): RewardRowValues => ({
    id: r.id,
    bonus_name: r.bonus_name ?? r.name ?? '',
    category: r.category ?? '',
    org_chart_node_id: r.org_chart_node_id ? String(r.org_chart_node_id) : '',
    bonus_target: r.bonus_target ?? r.value ?? '',
    bonus_amount: r.bonus_amount ?? '',
    bonus_period: r.bonus_period ?? '',
    is_published: r.is_published ?? false,
  });

  const form = useForm<RewardFormValues>({
    defaultValues: { items: [] },
  });

  const { fields, append, remove, replace } = useFieldArray({ control: form.control, name: 'items' });

  // defaultValues is only read once on mount; re-initialize when server data arrives.
  // Use the field array's replace so the rendered rows update reliably.
  useEffect(() => {
    if (rewards.length > 0) {
      replace(rewards.map(toRowValues));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards]);

  const handleAddRow = () => {
    append({
      bonus_name: '',
      category: '',
      org_chart_node_id: '',
      bonus_target: '',
      bonus_amount: '',
      bonus_period: '',
      is_published: false,
    });
  };

  const handleRemoveRow = (index: number) => {
    const item = form.getValues(`items.${index}`);
    if (item.id) setDeletedIds(prev => [...prev, item.id!]);
    remove(index);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!currentTenant?.id) return false;

    const data = form.getValues();
    const errors = formatValidationErrors(data.items);
    if (errors.hasErrors) {
      setValidationErrors(errors);
      toast.error(`${errors.summary}. Mohon perbaiki kesalahan di bawah.`);
      return false;
    }

    setValidationErrors(formatValidationErrors([]));
    setIsSaving(true);
    try {
      for (const id of deletedIds) {
        await deleteReward(currentTenant.id, id);
      }
      for (const item of data.items) {
        const payload: Partial<IReward> = {
          bonus_name: item.bonus_name?.trim(),
          bonus_period: item.bonus_period?.trim() ?? '',
          bonus_target: item.bonus_target?.trim() ?? '',
          bonus_amount: item.bonus_amount?.trim() ?? '',
          is_published: item.is_published ?? false,
          category: item.category,
          org_chart_node_id:
            item.category === 'level_jabatan' && item.org_chart_node_id ? Number(item.org_chart_node_id) : undefined,
        };
        if (item.id) {
          await updateReward(currentTenant.id, item.id, payload);
        } else {
          await createReward(currentTenant.id, payload);
        }
      }
      setDeletedIds([]);
      await getRewards(currentTenant.id, { page_size: 100 });
      await refetchCompletionStatus();
      toast.success('Reward berhasil disimpan');
      return true;
    } catch {
      toast.error('Gagal menyimpan reward');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (tab === 'reward' && !isReadOnly) {
      const ok = await handleSave();
      if (!ok) return;
    }
    navigate(`/${tenant_id}/sop`);
  };

  const tabClass = (active: boolean) =>
    clsx(
      'inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors',
      active
        ? 'bg-primary text-white'
        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
    );

  return (
    <section className="w-full dark:bg-[#0D0D0D] min-h-screen dark:text-white">
      {/* Page header */}
      <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 shadow-sm">
        <div className="flex flex-col">
          <h1 className="font-semibold text-lg dark:text-white">Reward &amp; Punishment (Penghargaan &amp; Sanksi)</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Anda akan merancang Peraturan Perusahaan, Tata Tertib, Skema Bonus, Skema Denda/Hukuman, dan Surat
            Peringatan.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPanel(v => !v)}
            className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronRight size={15} className={`transition-transform duration-200 ${showPanel ? 'rotate-90' : ''}`} />
            {showPanel ? 'Sembunyikan Panduan' : 'Tampilkan Panduan'}
          </button>

          <button
            type="button"
            onClick={handleSaveAndNext}
            disabled={isSaving}
            className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg text-white font-medium transition-opacity disabled:opacity-60"
            style={{ background: '#FD6118' }}
          >
            {isSaving ? 'Menyimpan...' : 'Simpan & Lanjut 🚀'}
          </button>
        </div>
      </div>

      {/* Tab buttons */}
      <div className="flex flex-wrap gap-3 px-5 pt-5">
        <button type="button" onClick={() => setTab('reward')} className={tabClass(tab === 'reward')}>
          🏆 Skema Bonus/Reward
        </button>
        <button type="button" onClick={() => setTab('punishment')} className={tabClass(tab === 'punishment')}>
          ⚠️ Kedisiplinan &amp; SP/Punishment
        </button>
      </div>

      {/* Content area */}
      <div className="flex flex-col xl:flex-row gap-6 p-5">
        <div className="flex-1 min-w-0">
          {tab === 'reward' ? (
            <div>
              {validationErrors.hasErrors && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex gap-3">
                  <AlertCircle className="text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <h4 className="font-semibold text-red-900 dark:text-red-200">Mohon perbaiki kesalahan berikut:</h4>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{validationErrors.summary}</p>
                  </div>
                </div>
              )}
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-[#1A1A1A]">
                {/* Table */}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px] text-sm border-collapse table-fixed">
                    <colgroup>
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '16%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '10%' }} />
                      {!isReadOnly && <col style={{ width: '48px' }} />}
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50 dark:bg-[#111111] border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Nama Bonus/Reward <span className="text-red-400">*</span>
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Kategori <span className="text-red-400">*</span>
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Jabatan (Jika Kategori Jabatan)
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Syarat Pencapaian <span className="text-red-400">*</span>
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Nominal/Jumlah Reward <span className="text-red-400">*</span>
                          {/* Kolomnya sudah menerima teks bebas, termasuk persentase.
                              Yang membuat pelapor trial mengira hanya rupiah yang bisa
                              diisi adalah contohnya, bukan batasan teknis. */}
                          <span className="block mt-0.5 text-[10px] font-normal normal-case text-gray-400 dark:text-gray-500">
                            Boleh nominal atau persentase
                          </span>
                        </th>
                        <th className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 text-left uppercase tracking-wide">
                          Periode
                        </th>
                        {!isReadOnly && <th className="px-2 py-3" />}
                      </tr>
                    </thead>
                    <tbody>
                      {fields.length === 0 && (
                        <tr>
                          <td
                            colSpan={isReadOnly ? 6 : 7}
                            className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500"
                          >
                            Belum ada data reward. Klik "+ Tambah Reward" untuk menambahkan.
                          </td>
                        </tr>
                      )}
                      {fields.map((field, index) => (
                        <RewardTableRow
                          key={field.id}
                          index={index}
                          form={form}
                          isReadOnly={isReadOnly}
                          orgChartNames={orgChartNames}
                          onRemove={() => handleRemoveRow(index)}
                          errorsByRow={validationErrors.errorsByRow}
                          errorsByRowAndField={validationErrors.errorsByRowAndField}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                {!isReadOnly && (
                  <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={handleAddRow}
                      className="flex items-center gap-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <Plus size={14} />
                      Tambah Reward
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <Punishment />
          )}
        </div>

        {showPanel && (
          <div className="w-full xl:w-80 shrink-0 min-w-0">
            <StepTipsPanel tips={tips} videoLink={videoLink} darkBackground />
          </div>
        )}
      </div>
    </section>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DynamicTextarea = ({ registerName, register, value, disabled, placeholder, className }: any) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className="relative w-full">
      <textarea
        {...register(registerName, {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onBlur: (e: any) => {
            e.target.scrollTop = 0;
            setIsFocused(false);
          },
        })}
        onFocus={() => setIsFocused(true)}
        disabled={disabled}
        placeholder={placeholder}
        rows={isFocused ? 3 : 1}
        className={
          className.replace('h-9', '') +
          (isFocused ? ' min-h-[36px] overflow-auto' : ' h-9 overflow-hidden') +
          ' block py-1.5 resize-none transition-all duration-200'
        }
        style={{ color: isFocused ? 'inherit' : 'transparent' }}
      />
      {!isFocused && (
        <div
          className="absolute inset-0 pointer-events-none px-3 flex items-center overflow-hidden text-sm"
          style={{ opacity: disabled ? 0.5 : 1 }}
        >
          <span className="truncate w-full text-gray-900 dark:text-white">
            {value || <span className="text-gray-400 dark:text-gray-500">{placeholder}</span>}
          </span>
        </div>
      )}
    </div>
  );
};

interface RewardTableRowProps {
  index: number;
  form: ReturnType<typeof useForm<RewardFormValues>>;
  isReadOnly: boolean;
  orgChartNames: { id: number; name: string }[];
  onRemove: () => void;
  errorsByRow?: Record<number, string[]>;
  errorsByRowAndField?: Record<number, Record<string, string>>;
}

function RewardTableRow({
  index,
  form,
  isReadOnly,
  orgChartNames,
  onRemove,
  errorsByRow = {},
  errorsByRowAndField = {},
}: RewardTableRowProps) {
  const { register, control, watch } = form;
  const category = watch(`items.${index}.category`);
  const rowErrors = errorsByRow[index] || [];
  const hasRowError = rowErrors.length > 0;
  const fieldErrors = errorsByRowAndField[index] || {};
  const errorTextClass = 'mt-1 text-xs text-red-600 dark:text-red-400';

  const cellClass = 'px-4 py-2 align-middle border-b border-gray-100 dark:border-gray-700';
  const inputClass =
    'text-sm w-full h-9 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-[#0D0D0D] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-500';

  return (
    <tr
      className={`align-top ${
        hasRowError ? 'bg-red-50/50 dark:bg-red-900/10' : 'bg-white dark:bg-[#1A1A1A]'
      } hover:bg-gray-50 dark:hover:bg-[#222222]`}
    >
      {/* Nama Bonus */}
      <td className={cellClass}>
        <DynamicTextarea
          registerName={`items.${index}.bonus_name`}
          register={register}
          value={watch(`items.${index}.bonus_name`)}
          disabled={isReadOnly}
          placeholder="contoh: Bonus Omset Bulanan"
          className={inputClass}
        />
        {fieldErrors.bonus_name && <p className={errorTextClass}>{fieldErrors.bonus_name}</p>}
      </td>

      {/* Kategori */}
      <td className={cellClass}>
        <Controller
          control={control}
          name={`items.${index}.category`}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              disabled={isReadOnly}
              className={`${inputClass} appearance-none`}
            >
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        />
        {fieldErrors.category && <p className={errorTextClass}>{fieldErrors.category}</p>}
      </td>

      {/* Jabatan */}
      <td className={cellClass}>
        <Controller
          control={control}
          name={`items.${index}.org_chart_node_id`}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              disabled={isReadOnly || category !== 'level_jabatan'}
              className={`${inputClass} appearance-none`}
            >
              <option value="">-- Pilih Jabatan</option>
              {orgChartNames.map(node => (
                <option key={node.id} value={String(node.id)}>
                  {node.name}
                </option>
              ))}
            </select>
          )}
        />
        {fieldErrors.org_chart_node_id && <p className={errorTextClass}>{fieldErrors.org_chart_node_id}</p>}
      </td>

      {/* Syarat Pencapaian (bonus_target) */}
      <td className={cellClass}>
        <DynamicTextarea
          registerName={`items.${index}.bonus_target`}
          register={register}
          value={watch(`items.${index}.bonus_target`)}
          disabled={isReadOnly}
          placeholder="contoh: Tercapainya target omset bulanan 100%"
          className={inputClass}
        />
        {fieldErrors.bonus_target && <p className={errorTextClass}>{fieldErrors.bonus_target}</p>}
      </td>

      {/* Nominal/Jumlah Reward (bonus_amount) */}
      <td className={cellClass}>
        <DynamicTextarea
          registerName={`items.${index}.bonus_amount`}
          register={register}
          value={watch(`items.${index}.bonus_amount`)}
          disabled={isReadOnly}
          placeholder="contoh: Rp 500.000 atau 5% dari laba"
          className={inputClass}
        />
        {fieldErrors.bonus_amount && <p className={errorTextClass}>{fieldErrors.bonus_amount}</p>}
      </td>

      {/* Periode */}
      <td className={cellClass}>
        <Controller
          control={control}
          name={`items.${index}.bonus_period`}
          render={({ field }) => (
            <select
              value={field.value}
              onChange={field.onChange}
              disabled={isReadOnly}
              className={`${inputClass} appearance-none`}
            >
              {PERIOD_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
        />
      </td>

      {/* Delete */}
      {!isReadOnly && (
        <td className={`${cellClass} text-center`}>
          <button
            type="button"
            onClick={onRemove}
            className="p-1 rounded-full text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
            title="Hapus baris"
          >
            <Trash2 size={16} />
          </button>
        </td>
      )}
    </tr>
  );
}
